import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { selectUser, getUser } from "../../redux/modules/userSlice";
import { UserAPI } from "../api";
import { AUTH_COLORS, AUTH_FONT } from "../../config/authConstants";
import {
  JOINEE_FORM_LIST,
  formatJoineeValue,
  getJoineeFormComment,
  isJoineeFormDone,
  isJoineeFormIncomplete,
  isJoineeFormUnlocked,
  isNewJoineeFormSubmitted,
  joineeFormStatusLabel,
} from "../../config/joineeConstants";
import JoineeLayout from "./JoineeLayout";
import ProfileCropDialog from "./ProfileCropDialog";
import HrResubmitNotice from "./HrResubmitNotice";
import profileCardBg from "../../Assests/Images/joinee/profile-card-bg.jpg";
import avatarPlaceholder from "../../Assests/Images/joinee/avatar-placeholder.png";
import iconPencil from "../../Assests/Images/joinee/icon-pencil.svg";
import iconLock from "../../Assests/Images/joinee/icon-lock.svg";

var JOINEE_NAVY = "#050A44";
var FORM_TITLE = "#0F172A";
var INFO_BLUE = "#3F85F5";
var ICON_BORDER = "#E2E8F0";

function displayPhone(phone) {
  if (phone === null || phone === undefined || phone === "") {
    return formatJoineeValue(phone);
  }
  return String(phone);
}

function formatAadhaar(value) {
  if (value === null || value === undefined || value === "") {
    return formatJoineeValue(value);
  }
  var digits = String(value).replace(/\D/g, "");
  if (!digits) {
    return formatJoineeValue(value);
  }
  var parts = [];
  for (var i = 0; i < digits.length; i += 4) {
    parts.push(digits.slice(i, i + 4));
  }
  return parts.join(" ");
}

function formatDoj(value) {
  if (!value) {
    return formatJoineeValue(value);
  }
  var raw = String(value);
  var parsed = new Date(raw);
  if (!isNaN(parsed.getTime()) && raw.indexOf("T") !== -1) {
    return parsed.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return raw;
}

function hasUploadedPhoto(photo) {
  return Boolean(photo) && photo !== avatarPlaceholder;
}

function IconBadge(props) {
  return (
    <Box
      className="joinee-icon-badge"
      sx={{
        width: 32,
        height: 32,
        borderRadius: "24px",
        border: "1px solid " + ICON_BORDER,
        bgcolor: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      }}
    >
      {props.children}
    </Box>
  );
}

function JoineeHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector(selectUser);
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [pendingFileName, setPendingFileName] = useState("profile.jpg");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(
    function () {
      if (!user || !user._id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      UserAPI.getUser(user._id)
        .then(function (res) {
          var data = res && res.data ? res.data : res;
          if (data) {
            setProfile(data);
            dispatch(getUser(Object.assign({}, user, data)));
          }
          setLoading(false);
        })
        .catch(function () {
          setProfile(user);
          setLoading(false);
        });
    },
    [user && user._id, location.pathname]
  );

  var source = profile || user || {};
  var photoUploaded = hasUploadedPhoto(source.profilePhoto);

  var profileRows = [
    { label: "Employee Name:", value: formatJoineeValue(source.name) },
    { label: "Personal Email ID:", value: formatJoineeValue(source.email) },
    {
      label: "Company Name:",
      value: formatJoineeValue(source.companyName),
    },
    { label: "Designation:", value: formatJoineeValue(source.designation) },
    { label: "Date of Join:", value: formatDoj(source.doj) },
    {
      label: "Work Location:",
      value: formatJoineeValue(source.workLocation),
    },
    { label: "Aadhaar No.:", value: formatAadhaar(source.aadhaar) },
    { label: "PAN No.:", value: formatJoineeValue(source.pan) },
    {
      label: "Phone No.:",
      value: displayPhone(source.phoneNumber),
    },
  ];

  var allFormsReady = JOINEE_FORM_LIST.every(function (form) {
    return isJoineeFormDone(source[form.statusKey]);
  });

  var allFormsApproved = JOINEE_FORM_LIST.every(function (form) {
    return source[form.statusKey] === "review done";
  });

  var pendingResubmitCount = JOINEE_FORM_LIST.filter(function (form) {
    return source[form.statusKey] === "resubmission required";
  }).length;

  var newJoineeSubmitted = isNewJoineeFormSubmitted(
    source.isNewJoineeFormComplete
  );

  var firstIncompleteIndex = -1;
  for (var fi = 0; fi < JOINEE_FORM_LIST.length; fi++) {
    if (
      isJoineeFormUnlocked(source, JOINEE_FORM_LIST[fi]) &&
      isJoineeFormIncomplete(source[JOINEE_FORM_LIST[fi].statusKey])
    ) {
      firstIncompleteIndex = fi;
      break;
    }
  }

  function handleOpenForm(form) {
    if (!isJoineeFormUnlocked(source, form)) {
      toast.info("Please submit the New Joinee Form first to unlock this form.", {
        hideProgressBar: true,
        closeOnClick: true,
      });
      return;
    }
    navigate(form.path);
  }

  function handleUploadClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function handleAvatarClick() {
    if (photoUploaded) {
      setPreviewOpen(true);
    }
  }

  function handleFileChange(event) {
    var file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    if (!file.type || file.type.indexOf("image/") !== 0) {
      toast.error("Please select an image file", {
        hideProgressBar: true,
        closeOnClick: true,
      });
      event.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB", {
        hideProgressBar: true,
        closeOnClick: true,
      });
      event.target.value = "";
      return;
    }
    setPendingFileName(file.name || "profile.jpg");
    var reader = new FileReader();
    reader.onload = function (e) {
      setCropSrc(e.target.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  }

  function handleCropClose() {
    if (uploading) {
      return;
    }
    setCropOpen(false);
    setCropSrc(null);
  }

  function handleCropConfirm(dataUrl) {
    if (!dataUrl || !source._id) {
      toast.error("Unable to crop image. Please try again.", {
        hideProgressBar: true,
        closeOnClick: true,
      });
      return;
    }
    setUploading(true);
    UserAPI.updateProfilePhoto(source._id, {
      profilePhoto: dataUrl,
      profilePhoto_fileName: pendingFileName,
    })
      .then(function (res) {
        setUploading(false);
        if (!res || res.success === false) {
          toast.error(
            (res && res.message) || "Unable to upload profile photo",
            {
              hideProgressBar: true,
              closeOnClick: true,
            }
          );
          return;
        }
        var payload = res.data ? res.data : null;
        var nextPhoto =
          payload && payload.profilePhoto ? payload.profilePhoto : dataUrl;
        var next = Object.assign({}, source, {
          profilePhoto: nextPhoto,
        });
        if (payload) {
          next = Object.assign({}, next, payload);
        }
        setProfile(next);
        dispatch(getUser(Object.assign({}, user, next)));
        setCropOpen(false);
        setCropSrc(null);
        toast.success("Profile photo updated", {
          hideProgressBar: true,
          closeOnClick: true,
        });
      })
      .catch(function () {
        setUploading(false);
        toast.error("Unable to upload profile photo", {
          hideProgressBar: true,
          closeOnClick: true,
        });
      });
  }

  function statusSubtitle(form, status) {
    if (form.showSectionProgress && status === "draft") {
      return "Draft — continue filling";
    }
    if (form.showSectionProgress && status === "resubmission required") {
      return "Action needed — resubmit";
    }
    if (
      form.showSectionProgress &&
      (status === "pending" || !status) &&
      isJoineeFormIncomplete(status)
    ) {
      return "Not started";
    }
    return joineeFormStatusLabel(status);
  }

  function renderActionIcon(status, locked) {
    if (locked) {
      return (
        <IconBadge>
          <Box
            component="img"
            src={iconLock}
            alt=""
            sx={{ width: 15, height: 15 }}
          />
        </IconBadge>
      );
    }
    if (status === "resubmission required") {
      return (
        <IconBadge>
          <Box
            component="img"
            src={iconPencil}
            alt=""
            sx={{ width: 15, height: 15 }}
          />
        </IconBadge>
      );
    }
    if (isJoineeFormDone(status)) {
      return (
        <IconBadge>
          <CheckIcon sx={{ fontSize: 17, color: "#22C55E" }} />
        </IconBadge>
      );
    }
    return (
      <IconBadge>
        <Box
          component="img"
          src={iconPencil}
          alt=""
          sx={{ width: 15, height: 15 }}
        />
      </IconBadge>
    );
  }

  if (loading) {
    return (
      <JoineeLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: AUTH_COLORS.primary }} />
        </Box>
      </JoineeLayout>
    );
  }

  return (
    <JoineeLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: "16px", sm: "20px", lg: "28px" },
          alignItems: "stretch",
          justifyContent: "center",
          width: "100%",
          maxWidth: 1180,
          maxHeight: { lg: "min(720px, calc(100dvh - 160px))" },
          "@supports not (height: 100dvh)": {
            maxHeight: { lg: "min(720px, calc(100vh - 160px))" },
          },
          mx: "auto",
          my: 0,
        }}
      >
        {/* Left — profile */}
        <Box
          sx={{
            flex: { xs: "1 1 auto", lg: "1 1 0" },
            minWidth: 0,
            width: "100%",
            maxHeight: { lg: "100%" },
            overflow: "hidden",
            border: "1px solid " + AUTH_COLORS.border,
            borderRadius: "12px",
            bgcolor: AUTH_COLORS.surface,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 4px 24px rgba(5, 10, 68, 0.06)",
          }}
        >
          {/* Top: upload only — wave background */}
          <Box
            sx={{
              position: "relative",
              flexShrink: 0,
              px: { xs: 2.5, sm: 3.5 },
              pt: 2.5,
              pb: 2.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflow: "hidden",
              borderBottom: "1px solid " + AUTH_COLORS.border,
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
              }}
            >
              <Box
                component="img"
                src={profileCardBg}
                alt=""
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(255,255,255,0.42)",
                }}
              />
            </Box>

            <Stack
              spacing={2}
              alignItems="center"
              sx={{
                width: 260,
                maxWidth: "100%",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                onClick={handleAvatarClick}
                role={photoUploaded ? "button" : undefined}
                tabIndex={photoUploaded ? 0 : undefined}
                onKeyDown={function (e) {
                  if (
                    photoUploaded &&
                    (e.key === "Enter" || e.key === " ")
                  ) {
                    e.preventDefault();
                    handleAvatarClick();
                  }
                }}
                sx={{
                  cursor: photoUploaded ? "pointer" : "default",
                  borderRadius: "50%",
                  outline: "none",
                  "&:focus-visible": photoUploaded
                    ? {
                        boxShadow: "0 0 0 3px rgba(5, 10, 68, 0.35)",
                      }
                    : undefined,
                  "&:hover .avatar-view-hint": photoUploaded
                    ? { opacity: 1 }
                    : undefined,
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src={source.profilePhoto || avatarPlaceholder}
                    alt={source.name || "Profile"}
                    sx={{
                      width: 120,
                      height: 120,
                      border: "2px solid rgba(255,255,255,0.95)",
                      boxShadow: "0 2px 12px rgba(5, 10, 68, 0.14)",
                      bgcolor: "#E8ECF0",
                    }}
                  />
                  {photoUploaded ? (
                    <Box
                      className="avatar-view-hint"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(5, 10, 68, 0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s ease",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: AUTH_FONT,
                          fontSize: 13,
                          fontWeight: 400,
                          color: "#fff",
                        }}
                      >
                        View
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <Button
                fullWidth
                variant="outlined"
                disabled={uploading}
                onClick={handleUploadClick}
                sx={{
                  fontFamily: AUTH_FONT,
                  fontSize: 15,
                  textTransform: "none",
                  color: AUTH_COLORS.text,
                  borderColor: AUTH_COLORS.border,
                  borderRadius: "6px",
                  minHeight: 34,
                  bgcolor: AUTH_COLORS.surface,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: JOINEE_NAVY,
                    bgcolor: "#F5F7FF",
                    color: JOINEE_NAVY,
                  },
                }}
              >
                {uploading
                  ? "Uploading..."
                  : photoUploaded
                  ? "Change Profile"
                  : "Upload Profile"}
              </Button>
            </Stack>
          </Box>

          {/* Bottom: employee details — plain, no wave */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: { xs: 2.5, sm: 3.5 },
              py: { xs: 2.25, sm: 2.75 },
              bgcolor: AUTH_COLORS.surface,
            }}
          >
            <Stack
              spacing={1.75}
              sx={{ width: "100%", maxWidth: 420, mx: "auto" }}
            >
              {profileRows.map(function (row) {
                return (
                  <Box
                    key={row.label}
                    sx={{
                      display: "flex",
                      gap: { xs: 1.5, sm: 2.5 },
                      alignItems: "flex-start",
                      fontFamily: AUTH_FONT,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        width: { xs: 130, sm: 150 },
                        flexShrink: 0,
                        fontFamily: AUTH_FONT,
                        fontSize: { xs: 14, sm: 16 },
                        lineHeight: 1.4,
                        color: "#848F9A",
                        fontWeight: 400,
                      }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: AUTH_FONT,
                        fontSize: { xs: 14, sm: 16 },
                        lineHeight: 1.4,
                        color: "#171A1C",
                        wordBreak: "break-word",
                        fontWeight: 400,
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>

        {/* Right — forms list */}
        <Box
          sx={{
            flex: { xs: "1 1 auto", lg: "1 1 0" },
            minWidth: 0,
            width: "100%",
            maxHeight: { lg: "100%" },
            overflow: "hidden",
            border: "1px solid " + AUTH_COLORS.border,
            borderRadius: "12px",
            bgcolor: AUTH_COLORS.surface,
            p: { xs: 2.25, sm: 2.75 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            boxShadow: "0 4px 24px rgba(5, 10, 68, 0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: AUTH_FONT,
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 400,
                  color: "#171A1C",
                  lineHeight: 1.35,
                }}
              >
                Please complete the forms below:
              </Typography>
              <Typography
                sx={{
                  fontFamily: AUTH_FONT,
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 400,
                  color: AUTH_COLORS.textSecondary,
                  lineHeight: 1.4,
                  mt: 0.5,
                }}
              >
                {pendingResubmitCount > 0
                  ? pendingResubmitCount +
                    " form(s) need your attention — open them to see HR feedback."
                  : allFormsApproved
                  ? "All forms approved by HR. No further action needed."
                  : allFormsReady
                  ? "All forms submitted. HR will review each form and update the status."
                  : !newJoineeSubmitted
                  ? "Start with the New Joinee Form. The other forms unlock once you submit it."
                  : "Complete and submit each form. HR will review and approve or request changes."}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              border: "1px solid " + AUTH_COLORS.borderSecondary,
              borderRadius: "6px",
              p: { xs: "14px", sm: "18px" },
              bgcolor: AUTH_COLORS.surface,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {JOINEE_FORM_LIST.map(function (form, index) {
              var status = source[form.statusKey];
              var isDone = isJoineeFormDone(status);
              var needsResubmit = status === "resubmission required";
              var hrComment = getJoineeFormComment(source, form);
              var isCurrent = index === firstIncompleteIndex;
              var locked = !isJoineeFormUnlocked(source, form);
              var subtitle = locked
                ? "Locked — submit the New Joinee Form first"
                : statusSubtitle(form, status);
              return (
                <Box key={form.id}>
                  {index > 0 ? (
                    <Divider
                      sx={{
                        my: "9px",
                        borderColor: AUTH_COLORS.border,
                      }}
                    />
                  ) : null}
                  <Box
                    onClick={function () {
                      handleOpenForm(form);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-disabled={locked || undefined}
                    title={locked ? "Submit the New Joinee Form to unlock" : undefined}
                    onKeyDown={function (e) {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenForm(form);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: locked ? "not-allowed" : "pointer",
                      opacity: locked ? 0.6 : 1,
                      borderRadius: "8px",
                      px: "6px",
                      py: "3px",
                      mx: "-6px",
                      bgcolor: "transparent",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        bgcolor: locked ? "transparent" : "rgba(5, 10, 68, 0.04)",
                      },
                      "&:hover .joinee-icon-badge": locked
                        ? {}
                        : {
                            borderColor: JOINEE_NAVY,
                            boxShadow: "0 0 0 2px rgba(5, 10, 68, 0.08)",
                          },
                      "&:focus-visible": {
                        outline: "2px solid " + JOINEE_NAVY,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                        gap: "10px",
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "20px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: isCurrent
                            ? JOINEE_NAVY
                            : AUTH_COLORS.surface,
                          border: isCurrent
                            ? "none"
                            : "1px solid " + AUTH_COLORS.border,
                          color: isCurrent
                            ? AUTH_COLORS.buttonLabel
                            : AUTH_COLORS.textSecondary,
                          fontFamily: AUTH_FONT,
                          fontSize: 15,
                          fontWeight: 400,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: AUTH_FONT,
                            fontSize: { xs: 14, sm: 16 },
                            fontWeight: isCurrent ? 500 : 400,
                            color: FORM_TITLE,
                            lineHeight: 1.3,
                          }}
                        >
                          {form.name}
                        </Typography>
                        {subtitle ? (
                          <Typography
                            sx={{
                              fontFamily: AUTH_FONT,
                              fontSize: { xs: 11, sm: 13 },
                              fontWeight: 400,
                              color: needsResubmit
                                ? "#D97706"
                                : isDone
                                ? "#16A34A"
                                : isCurrent
                                ? JOINEE_NAVY
                                : AUTH_COLORS.textSecondary,
                              lineHeight: 1.25,
                              mt: 0.25,
                            }}
                          >
                            {subtitle}
                          </Typography>
                        ) : null}
                        {needsResubmit ? (
                          <HrResubmitNotice comment={hrComment} compact />
                        ) : null}
                      </Box>
                    </Box>
                    {renderActionIcon(status, locked)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <ProfileCropDialog
        open={cropOpen}
        imageSrc={cropSrc}
        saving={uploading}
        onClose={handleCropClose}
        onConfirm={handleCropConfirm}
      />

      <Dialog
        open={previewOpen}
        onClose={function () {
          setPreviewOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            bgcolor: "#0B0F1A",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            bgcolor: "#111827",
          }}
        >
          <Typography
            sx={{
              fontFamily: AUTH_FONT,
              fontSize: 15,
              fontWeight: 400,
              color: "#F9FAFB",
            }}
          >
            Profile photo
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={function () {
              setPreviewOpen(false);
            }}
            size="small"
            sx={{ color: "#F9FAFB" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#0B0F1A",
          }}
        >
          <Box
            component="img"
            src={source.profilePhoto}
            alt={source.name || "Profile photo"}
            sx={{
              maxWidth: "100%",
              maxHeight: "70vh",
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
    </JoineeLayout>
  );
}

export default JoineeHome;
